/**
 * Pure view helpers for a portal invoice list — tenant AND landlord.
 *
 * Separate from the pages so the money- and status-wording rules can be tested
 * without rendering, and so a mutation to one of them fails a small, named test
 * instead of a large render assertion. Mirrors the existing
 * `utils/tenancyStatusLabels.js` convention for the external portals.
 *
 * SHARED ON PURPOSE (INV-RETIRE.3f). These functions were written for the tenant
 * portal in 3c and moved here unchanged when the landlord portal needed the same
 * vocabulary. Copying them would have created a second status matrix free to
 * drift, and a landlord and a tenant looking at the same invoice must not read
 * two different words for it. `pages/tenant/tenantInvoiceView.js` re-exports this
 * module so the tenant surface and its tests are untouched.
 *
 * NOTHING HERE KNOWS WHERE AN INVOICE CAME FROM. The portal endpoints already
 * answer that question and deliberately do not pass it on; a helper that branched
 * on the source would re-introduce the distinction the backend spent three slices
 * removing.
 */

/**
 * The four statuses the portal contract can produce
 * (`PortalInvoiceStatus` in backend/app/schemas/portal_invoice.py).
 */
const INVOICE_STATUS_LABELS_DE = {
  paid: "Bezahlt",
  overdue: "Überfällig",
  unpaid: "Offen",
  cancelled: "Storniert",
};

/**
 * German label for a portal invoice status.
 *
 * Anything outside the contract becomes "Unbekannt" — NOT "Offen". The previous
 * version of this page used `Offen` as its catch-all, which meant a cancelled
 * invoice told the tenant they still owed money. An unknown status is a display
 * problem; presenting it as an open debt is a false financial statement, and the
 * quiet version of that is worse than the visible one.
 *
 * The raw value is not passed through either (unlike `tenancyStatusLabelDe`):
 * provider vocabulary such as `pending` would then reach a tenant who is not
 * supposed to learn that a provider exists.
 */
export function invoiceStatusLabelDe(status) {
  if (typeof status !== "string" || !status) return "Unbekannt";
  return INVOICE_STATUS_LABELS_DE[status] || "Unbekannt";
}

/**
 * Colour for a status, using only the palette already present in this portal.
 *
 * Colour is decoration here, never the message: every status also renders its
 * German word, so the information survives for a reader who cannot see the hue.
 */
export function invoiceStatusStyle(status) {
  if (status === "paid") return { color: "#15803D", fontWeight: 600 };
  if (status === "overdue") return { color: "#B91C1C", fontWeight: 600 };
  if (status === "cancelled") return { color: "#64748B", fontWeight: 600 };
  return { color: "#475569" };
}

/**
 * Amount with its currency code, or the bare amount when the currency is unknown.
 *
 * The mirror can hold an invoice whose currency the provider never confirmed
 * (nullable since migration 159), and the portal passes that through as null
 * rather than defaulting it. Printing "CHF" over a null would put a number and a
 * currency together that nobody verified belong together — on a document the
 * tenant may pay against. A bare, correctly formatted amount says less, and
 * everything it says is true.
 *
 * Native invoices are unaffected: `Invoice.currency` is NOT NULL with default
 * "CHF" (db/models.py), so they keep rendering exactly as before.
 *
 * Number formatting is unchanged from the previous implementation on purpose —
 * this slice is about the currency code, not about how many decimals a franc has.
 */
export function formatInvoiceAmount(amount, currency) {
  const n = Number(amount);
  if (Number.isNaN(n)) return "—";
  const formatted = n.toLocaleString("de-CH");
  const code = typeof currency === "string" ? currency.trim() : "";
  return code ? `${code} ${formatted}` : formatted;
}

/**
 * One message for every reason a document cannot be produced, matching the
 * backend's own tenant-facing wording (`_PDF_UNAVAILABLE_DE` in routes_tenant.py).
 *
 * The tenant never learns which layer failed. Whether a token expired, a
 * provider answered 404 or an object store rejected a read is an operator's
 * question, and the answer belongs in a log.
 */
export const PDF_UNAVAILABLE_DE = "Der Rechnungsbeleg ist momentan nicht verfügbar.";

/**
 * Accessible name for one row's document action.
 *
 * Every row's button reads "PDF öffnen", so the visible label alone leaves a
 * screen-reader user with a list of identical controls. The invoice number is
 * what distinguishes them.
 */
export function pdfActionLabel(invoiceNumber) {
  const label = (invoiceNumber || "").trim();
  return label ? `Rechnung ${label} als PDF öffnen` : "Rechnung als PDF öffnen";
}
